import React, {
	useRef,
	useEffect,
	useState,
	useCallback,
	useMemo,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type SyntheticEvent,
	type Ref,
} from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import EditFilledIcon from '@atlaskit/icon/core/migration/edit--edit-filled';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import AkTooltip from '@atlaskit/tooltip';
import type { Issue } from '@atlassian/jira-assign-issue-parent-modal/src/model/types.tsx';
import AsyncIcon from '@atlassian/jira-common-components-async-icon/src/view.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import componentWithCondition from '@atlassian/jira-common-util-component-with-condition/src/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount/OpTrigger.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import type { IssueParent } from '@atlassian/jira-issue-parent-services/src/services/types.tsx';
import { useHasSetParentEntitlementForAboveLevelOne } from '@atlassian/jira-issue-parent-services/src/services/utils/has-set-parent-entitlement-for-above-level-one/index.tsx';
import { DEFAULT_PARENT_ISSUE_TYPE_NAME } from '@atlassian/jira-issue-view-common-constants/src/issue-types.tsx';
import type { IssueType } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { epicDropdownOpenInteraction } from '@atlassian/jira-issue-view-common-utils/src/utils/performance-analytics/index.tsx';
import { BreadCrumbsButton } from '@atlassian/jira-issue-view-common-views/src/breadcrumbs-button/index.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { withNewGICProvider } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import type { GICPayload } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/types.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	AnalyticsEventToProps,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueId, RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';
import {
	globalIssueCreateTrigger,
	useSyncIssueParentsToParentFieldSuggestionCache,
	useAddToRecentlySelectedParents,
	useIssueParentsWithRecentlySelected,
	useSyncLoadingState,
} from './utils.tsx';

export const DropdownItemWithAnalytics = AnalyticsEventToProps('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export type Props = {
	isDisabled?: boolean;
	isOrphan?: boolean;
	issueTypes?: IssueType[];
	issues?: Issue[];
	issueParents?: IssueParent[];
	isOpen?: boolean;
	issueKey?: string;
	currentIssueKey?: string;
	issueTypeName?: string;
	issueTypeIconUrl?: string;
	issueHierarchyLevel?: number | null;
	clickRef?: Ref<HTMLElement | undefined> | null;
	projectId?: string | null;
	isSimplifiedProject: boolean;
	// eslint-disable-next-line jira/react/handler-naming
	fetchIssueParents?: () => void;
	text?: string;
	setIssueParent?: (issues: Issue[], parentId?: IssueId) => void;
	setIssueParentOptimistic?: (issues: Issue[], parent: IssueParent | null) => void;
	// eslint-disable-next-line jira/react/handler-naming
	openIssueCreateModal?: (data: GICPayload) => void;
	onOpenChange?: (isOpen: boolean) => void;
	onSelect?: () => void;
	onViewAllParentsClick?: () => void;
	onToggle?: (analyticsEvent: UIAnalyticsEvent) => void;
	onClick?: (analyticsEvent: UIAnalyticsEvent) => void;
	triggerRef?: Ref<HTMLElement>;
	// this prop is to notify IssueBreadcrumbs, when parent changes from/to empty value (focus back to trigger)
	onAddOrRemoveParent?: () => void;
};

export const IssueParentSwitcher = ({
	isDisabled = false,
	isOrphan = false,
	issueTypes = [],
	issues = [],
	issueParents = [],
	isOpen: isOpenInProps = false,
	issueKey = '',
	currentIssueKey = '',
	issueTypeName = '',
	issueTypeIconUrl = '',
	issueHierarchyLevel = 0,
	clickRef = null,
	projectId = null,
	isSimplifiedProject,
	fetchIssueParents = noop,
	setIssueParent = noop,
	setIssueParentOptimistic = noop,
	text = '',
	onOpenChange = noop,
	onSelect = noop,
	onViewAllParentsClick = noop,
	onToggle,
	onClick,
	openIssueCreateModal,
	triggerRef = { current: null },
	onAddOrRemoveParent = noop,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hasSetParentEntitlementForAboveLevelOne = useHasSetParentEntitlementForAboveLevelOne();
	const { setReturnFocusTo } = useModalDialogActions();

	const [isOpen, setIsOpen] = useState(isOpenInProps);
	const [isLoading, setIsLoading] = useState(!issueParents.length);

	const previousIsOpen = usePrevious(isOpenInProps);
	const previousIssueParents = usePrevious(issueParents);

	const [fieldConfig] = useFieldConfig(currentIssueKey, PARENT_TYPE);

	const [valueInSweetState] = useFieldValue({
		issueKey: issues[0].key,
		fieldKey: PARENT_TYPE,
	});
	const issueKeyInSweetState = valueInSweetState?.key || null;
	const isMounted = useRef(false);

	const hasFetched = previousIssueParents !== undefined && issueParents !== previousIssueParents;
	const issueParentsWithRecentlySelected = useIssueParentsWithRecentlySelected({
		issueParents,
		hasFetched,
	});

	useSyncLoadingState({
		isLoading,
		setIsLoading,
		issueParentsWithRecentlySelected,
	});

	useSyncIssueParentsToParentFieldSuggestionCache({
		isSimplifiedProject,
		hasFetched,
		issueParents,
		isOpen,
	});

	const addToRecentlySelectedParents = useAddToRecentlySelectedParents({ issueParents });

	if (!fg('relay-migration-issue-header-and-parent')) {
		// The below effect was used to sync inline editing backlog with issue view (sweet state and redux data sync)
		// We don't need this anymore with the new sync mechanisms (eg soft refresh or just everything using relay)
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (isMounted.current) {
				if (!valueInSweetState) {
					setIssueParentOptimistic(issues, null);
				} else if (issueKey !== valueInSweetState.key) {
					setIssueParentOptimistic(issues, {
						id: valueInSweetState.id,
						summary: valueInSweetState.fields.summary,
						key: valueInSweetState.key,
						issueType: {
							id: valueInSweetState.fields.issuetype.id,
							name: valueInSweetState.fields.issuetype.name,
							iconUrl: valueInSweetState.fields.issuetype.iconUrl,
						},
						color: valueInSweetState.color,
					});
				}
			}
			isMounted.current = true;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [issueKeyInSweetState]);
	}

	const toggleMenu = useCallback(
		(isOpenInArgs: boolean) => {
			setIsOpen(isOpenInArgs);

			if (!isOpenInArgs) return;

			if (
				!hasSetParentEntitlementForAboveLevelOne &&
				issueHierarchyLevel != null &&
				issueHierarchyLevel >= 1
			) {
				setIsLoading(false);
				return;
			}
			epicDropdownOpenInteraction.start();
			fetchIssueParents();
		},
		[fetchIssueParents, issueHierarchyLevel, hasSetParentEntitlementForAboveLevelOne],
	);

	useEffect(() => {
		if (previousIsOpen !== undefined) {
			if (isOpenInProps !== previousIsOpen && isOpenInProps !== isOpen) {
				toggleMenu(isOpenInProps);
			}
		}
		if (previousIssueParents !== undefined) {
			if (issueParents !== previousIssueParents) {
				// suppress isLoading state when issueParents are fetched
				isLoading && setIsLoading(false);
			}
		}
	}, [
		isLoading,
		isOpen,
		issueParents,
		isOpenInProps,
		previousIsOpen,
		previousIssueParents,
		toggleMenu,
	]);

	const handleOnItemClick = useCallback(
		(
			userAction: string,
			{
				parentId: newParentId,
				keepMenuOpen = false,
			}: {
				parentId?: IssueId;
				keepMenuOpen?: boolean;
			} = {},
		) =>
			(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
				const oldParentId = issues[0]?.parentId;

				switch (userAction) {
					case 'add parent':
					case 'change parent':
						if (fg('one_event_rules_them_all_fg')) {
							getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
								PARENT_TYPE,
								oldParentId ?? undefined,
								newParentId,
							);
						}
						addToRecentlySelectedParents &&
							newParentId &&
							addToRecentlySelectedParents(newParentId);
						setIssueParent(issues, newParentId);
						userAction === 'add parent' && onAddOrRemoveParent();
						break;

					case 'remove parent':
						if (fg('one_event_rules_them_all_fg')) {
							getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
								PARENT_TYPE,
								oldParentId,
								null,
							);
						}
						setIssueParent(issues);
						onAddOrRemoveParent();
						break;

					case 'open modal':
						onViewAllParentsClick();
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						setReturnFocusTo(triggerRef as RefObject<HTMLElement>);
						onAddOrRemoveParent();
						break;

					case 'create parent': {
						const issueTypeIdAsNumber = (
							issueTypes.find(({ name }) => name === issueTypeName) || {}
						).id;

						const issueTypeId = String(issueTypeIdAsNumber);

						if (projectId === null || !issueTypeId) {
							log.safeErrorWithoutCustomerData(
								'issue-parent-switcher.create.issue.failure',
								'Failed to create the first epic via global issue create',
								{
									projectId,
									issueTypeId,
								},
							);
						}

						globalIssueCreateTrigger(projectId, issueTypeIdAsNumber, openIssueCreateModal);
						break;
					}

					default:
						break;
				}
				onClick?.(analyticsEvent.update({ userAction }));
				onSelect && onSelect();
				!keepMenuOpen && toggleMenu(false);
			},
		[
			onClick,
			onSelect,
			toggleMenu,
			addToRecentlySelectedParents,
			setIssueParent,
			issues,
			onAddOrRemoveParent,
			onViewAllParentsClick,
			setReturnFocusTo,
			triggerRef,
			issueTypes,
			projectId,
			openIssueCreateModal,
			issueTypeName,
		],
	);

	const getMessageContent = useCallback(
		(epicMessage: MessageDescriptor, defaultMessage: MessageDescriptor, multipleTargets = true) => {
			const target =
				issueTypeName !== DEFAULT_PARENT_ISSUE_TYPE_NAME
					? ''
					: `${DEFAULT_PARENT_ISSUE_TYPE_NAME.toLowerCase()}${multipleTargets ? 's' : ''}`;

			return issueTypeName && issueHierarchyLevel === 0
				? formatMessage(epicMessage, { target })
				: formatMessage(defaultMessage);
		},
		[formatMessage, issueHierarchyLevel, issueTypeName],
	);

	const tooltipContent = useMemo(() => {
		if (fieldConfig?.value?.isEditable === false) {
			// no need to check for ifOrphan as the Add button is not shown when not editable
			return formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.uneditableParentIssueTermRefresh
					: messages.uneditableParent,
				{ issueTypeName },
			);
		}

		/**
		 * - For orphan issues in TMP projects, we display "Add <issueTypeName>"
		 * - For orphan issues in CMP projects, we display "Add parent"
		 *
		 * See https://hello.jira.atlassian.cloud/browse/JPO-25790 for future reference
		 *
		 * @returns MessageDescriptor
		 */
		if (isSimplifiedProject && issueTypeName && issueHierarchyLevel === 0) {
			const target =
				issueTypeName !== DEFAULT_PARENT_ISSUE_TYPE_NAME
					? issueTypeName
					: DEFAULT_PARENT_ISSUE_TYPE_NAME.toLowerCase();

			return formatMessage(
				isOrphan ? messages.triggerTooltipAddEpic : messages.triggerTooltipChangeEpic,
				{ issueTypeName, target },
			);
		}

		return formatMessage(
			isOrphan ? messages.triggerTooltipAddParent : messages.triggerTooltipChangeParent,
			{ issueTypeName: issueTypeName ? `${issueTypeName} - ` : '' },
		);
	}, [
		fieldConfig,
		formatMessage,
		isOrphan,
		isSimplifiedProject,
		issueHierarchyLevel,
		issueTypeName,
	]);

	const renderIssueParentItem = useCallback(
		(issueParent: IssueParent) => {
			const {
				id,
				key,
				summary,
				issueType: { name, iconUrl },
			} = issueParent;
			const icon = <AsyncIcon alt={name || ''} url={iconUrl} />;

			return (
				<DropdownItemWithAnalytics
					key={key}
					elemBefore={icon}
					testId="issue-view-foundation.issue-parent-switcher.dropdown-item-issue-parent"
					onClick={handleOnItemClick(`${isOrphan ? 'add' : 'change'} parent`, {
						parentId: id,
					})}
				>
					{key} {summary}
				</DropdownItemWithAnalytics>
			);
		},
		[handleOnItemClick, isOrphan],
	);

	const renderDropdownItemGroup = useCallback(
		(recentIssueParents: IssueParent[]) =>
			isLoading ? (
				<PreloaderContainer>
					<Spinner testId="issue-view-foundation.issue-parent-switcher.spinner" size="large" />
				</PreloaderContainer>
			) : (
				<DropdownItemGroup
					title={getMessageContent(messages.dropdownTitleEpic, messages.dropdownTitle)}
				>
					{recentIssueParents.map((item) => renderIssueParentItem(item))}
				</DropdownItemGroup>
			),
		[getMessageContent, isLoading, renderIssueParentItem],
	);

	const dropdownMenu = useMemo(() => {
		const target =
			issueTypeName && issueTypeName !== DEFAULT_PARENT_ISSUE_TYPE_NAME
				? issueTypeName
				: DEFAULT_PARENT_ISSUE_TYPE_NAME.toLowerCase();

		if (
			!hasSetParentEntitlementForAboveLevelOne &&
			issueHierarchyLevel !== null &&
			issueHierarchyLevel >= 1
		) {
			return (
				<>
					<NoEntitlementContainer>
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.noEntitlementSporkIssueTermRefresh
								: messages.noEntitlementSpork,
							{ target },
						)}
					</NoEntitlementContainer>
					{!isOrphan && (
						<DropdownItemGroup>
							<DropdownItemWithAnalytics onClick={handleOnItemClick('remove parent')}>
								{formatMessage(messages.unlinkParent)}
							</DropdownItemWithAnalytics>
						</DropdownItemGroup>
					)}
				</>
			);
		}

		const recentIssueParents = issueParentsWithRecentlySelected
			.filter((issueParent) => issueParent.key !== issueKey)
			.slice(0, 5);

		const showEmptyContainer =
			issueHierarchyLevel === 0 && !isLoading && !issueParentsWithRecentlySelected.length;

		return showEmptyContainer ? (
			<EmptyContainer>
				{formatMessage(messages.dropdownEmpty, { target })}
				<Button
					appearance="link"
					spacing="compact"
					onClick={handleOnItemClick('create parent', {
						keepMenuOpen: true,
					})}
					data-testId="issue.views.issue-base.foundation.issue-parent-switcher.empty-container-button"
					interactionName="auto-generated.jira-issue-view-foundation.issue-parent-switcher.button"
				>
					{target}
				</Button>
				.
			</EmptyContainer>
		) : (
			<>
				{(isLoading || recentIssueParents.length > 0) && (
					<SeparatorWrapper>{renderDropdownItemGroup(recentIssueParents)}</SeparatorWrapper>
				)}
				<DropdownItemGroup>
					{!isOrphan && !isNil(issueHierarchyLevel) && issueHierarchyLevel >= 0 && (
						<DropdownItemWithAnalytics onClick={handleOnItemClick('remove parent')}>
							{formatMessage(messages.unlinkParent)}
						</DropdownItemWithAnalytics>
					)}
					<DropdownItemWithAnalytics
						onClick={handleOnItemClick('open modal')}
						testId="issue-view-foundation.issue-parent-switcher.view-all"
					>
						{getMessageContent(messages.viewEpics, messages.viewParents)}
					</DropdownItemWithAnalytics>
				</DropdownItemGroup>
				{!isLoading && (
					<OpTrigger
						op={() => {
							epicDropdownOpenInteraction.stop();
						}}
					/>
				)}
			</>
		);
	}, [
		formatMessage,
		getMessageContent,
		handleOnItemClick,
		isLoading,
		isOrphan,
		issueHierarchyLevel,
		issueKey,
		issueParentsWithRecentlySelected,
		issueTypeName,
		renderDropdownItemGroup,
		hasSetParentEntitlementForAboveLevelOne,
	]);

	const handleOnOpenChange = useCallback(
		({ isOpen: isOpenInArgs, event }: OnOpenChangeArgs) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'toggled',
				actionSubject: 'dropdownMenu',
			});
			// @ts-expect-error - TS2339 - Property 'button' does not exist on type 'HTMLElement'.
			const insideBreadcrumb = event && clickRef?.current?.button.contains(event.target);

			if (fg('one_event_rules_them_all_fg')) {
				if (isOpenInArgs && issues.length === 1) {
					getUpdateAnalyticsFlowHelper().fireAnalyticsStart(PARENT_TYPE, {
						analytics: createAnalyticsEvent({}),
						attributes: {
							fieldType: FIELD_TYPE_MAP[PARENT_TYPE],
						},
					});
				}
			}

			if (issues[0] !== undefined) {
				analyticsEvent.update({ epicSelectedId: issues[0].parentId });
			}

			onToggle?.(analyticsEvent);
			toggleMenu(isOpenInArgs);
			onOpenChange && !insideBreadcrumb && onOpenChange(isOpenInArgs);
		},
		[createAnalyticsEvent, clickRef, issues, onToggle, toggleMenu, onOpenChange],
	);

	const triggerButton = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'triggerRef' implicitly has an 'any' type.
		({ triggerRef: ref, ...triggerProps }) => {
			const lTriggerButton = (
				<BreadCrumbsButton
					{...triggerProps}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					testId="issue.views.issue-base.foundation.issue-parent-switcher.button"
					// eslint-disable-next-line react-hooks/rules-of-hooks
					ref={useMergeRefs(ref, triggerRef)}
					aria-label={tooltipContent}
					aria-expanded={isOpen}
					isDisabled={isDisabled}
					spacing="compact"
					appearance="subtle"
					iconBefore={
						!isOrphan && issueTypeIconUrl ? (
							<AsyncIcon alt={issueTypeName || ''} url={issueTypeIconUrl} />
						) : (
							<EditFilledIcon LEGACY_size="small" label="" />
						)
					}
				>
					{text}
				</BreadCrumbsButton>
			);

			return (
				<Box
					xcss={
						isVisualRefreshEnabled() ? triggerButtonContainerStylesVR : triggerButtonContainerStyles
					}
				>
					{isOrphan ? (
						lTriggerButton
					) : (
						<AkTooltip
							position="top"
							content={tooltipContent}
							delay={0}
							/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
							testId="issue.views.issue-base.foundation.issue-parent-switcher.tooltip"
						>
							{lTriggerButton}
						</AkTooltip>
					)}
				</Box>
			);
		},
		[
			isDisabled,
			isOpen,
			isOrphan,
			issueTypeIconUrl,
			issueTypeName,
			text,
			tooltipContent,
			triggerRef,
		],
	);

	return (
		<DropdownMenu
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-parent-switcher.dropdown"
			isOpen={isOpen}
			trigger={triggerButton}
			onOpenChange={handleOnOpenChange}
		>
			{dropdownMenu}
		</DropdownMenu>
	);
};

IssueParentSwitcher.displayName = 'IssueParentSwitcher';
const IssueParentSwitcherWithIntl = IssueParentSwitcher;

type ConfigProps = JSX.LibraryManagedAttributes<
	typeof IssueParentSwitcherWithIntl,
	ComponentProps<typeof IssueParentSwitcherWithIntl>
>;

const IssueParentSwitcherWithGICProvider = withNewGICProvider<ConfigProps>(
	IssueParentSwitcherWithIntl,
);

export default componentWithCondition<ConfigProps>(
	() => fg('render_modern_global_issue_create_modal_experience'),
	IssueParentSwitcherWithGICProvider,
	IssueParentSwitcherWithIntl,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreloaderContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${21 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${23.375 * gridSize}px`,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${25 * gridSize}px`,
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button, & span': {
		padding: 0,
		margin: 0,
		verticalAlign: 'initial',
	},
});

const triggerButtonContainerStyles = xcss({
	paddingRight: 'space.050',
});

const triggerButtonContainerStylesVR = xcss({
	paddingRight: 'space.0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NoEntitlementContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${35 * gridSize}px`,
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SeparatorWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border')}`,
});
